import { useEffect, useState } from 'react';
import Image from 'components/shared/image';
import useIsMobile from 'hooks/utils/useIsMobile';
import SiteLink from 'components/shared/sitelink/sitelink';
import { addLocaleToHref } from 'lib/utils/helpers';
import { useRouter } from 'next/router';
import styles from './blog.module.scss';

const Blog = ({ data, locale }) => {
  const { categories, allTopics, blogArticles } = data;

  const [selectedCategory, setSelectedCategory] = useState(-1);
  const [filteredArticles, setFilteredArticles] = useState([...blogArticles]);
  const isMobile = useIsMobile();

  const decideArticleStyle = image => {
    const imageType = isMobile ? image?.mobile : image?.desktop;

    if (imageType?.height <= 1000) return styles.articleWrapperSmall;
    if (imageType?.height <= 1200) return styles.articleWrapperMedium;
    if (imageType?.height > 1400) return styles.articleWrapperLarge;

    return styles.articleWrapperSmall;
  };

  const decideArticleImageStyle = image => {
    const imageType = isMobile ? image?.mobile : image?.desktop;

    if (imageType?.height <= 1000) return styles.articleHeadlineImageSmall;
    if (imageType?.height <= 1200) return styles.articleHeadlineImageMedium;
    if (imageType?.height > 1400) return styles.articleHeadlineImageLarge;

    return styles.articleHeadlineImageSmall;
  };

  useEffect(() => {
    if (selectedCategory === -1) setFilteredArticles(blogArticles);
    else
      setFilteredArticles(blogArticles.filter(article => article.articleType === selectedCategory));
  }, [selectedCategory, blogArticles]);

  return (
    <section id="sectionBlog">
      {categories?.length > 1 && (
        <div className={styles.blogCategories}>
          <div
            role="tab"
            tabIndex={0}
            className={styles.categoryWrapper}
            onClick={() => setSelectedCategory(-1)}
            onKeyDown={() => setSelectedCategory(-1)}
          >
            <h3 className={styles.categoryName}>{allTopics}</h3>
            {selectedCategory === -1 && <div className={styles.selectedBar} />}
          </div>
          {categories.map(category => (
            <div
              key={category.categoryType}
              role="tab"
              tabIndex={0}
              className={styles.categoryWrapper}
              onClick={() => setSelectedCategory(category.categoryType)}
              onKeyDown={() => setSelectedCategory(category.categoryType)}
            >
              <h3 className={styles.categoryName}>{category.title}</h3>
              {selectedCategory === category.categoryType && <div className={styles.selectedBar} />}
            </div>
          ))}
        </div>
      )}
      <div className={styles.blogArticles}>
        {filteredArticles.map(article => (
          <BlogArticleCard
            locale={locale}
            data={article}
            decideArticleStyle={decideArticleStyle}
            decideArticleImageStyle={decideArticleImageStyle}
            isMobile={isMobile}
          />
        ))}
      </div>
    </section>
  );
};

const BlogArticleCard = ({
  locale,
  data,
  decideArticleStyle,
  decideArticleImageStyle,
  isMobile,
}) => {
  const router = useRouter();
  const { articleLink, headlineImage, articleType } = data;
  if (articleLink?.slug) {
    return (
      <div className={decideArticleStyle(headlineImage.image)}>
        <SiteLink locale={locale} href={addLocaleToHref(articleLink?.slug, locale, router)}>
          <div className={decideArticleImageStyle(headlineImage.image)}>
            <Image src={headlineImage.image} layout="fill" loading="eager" priority />
          </div>
          <h3 className={styles.categoryType}>{articleType}</h3>
          <h4 className={styles.articleTitle}>{articleLink?.title}</h4>
          {isMobile && <div className={styles.articleDivider} />}
        </SiteLink>
      </div>
    );
  }

  return null;
};

export default Blog;
